import { createFeature, createReducer, on } from '@ngrx/store';

import { CompanyMarginsActions } from '../actions/company-margins.actions';
import { CompanyMargin } from '../company-margins-state.model';
import { INITIAL_COMPANY_MARGINS_STATE } from '../company-margins.state';

function marginKey(margin: {
  readonly buyerId: number;
  readonly supplierId: number;
}): string {
  return `${margin.buyerId}:${margin.supplierId}`;
}

function mergeMargins(
  current: readonly CompanyMargin[],
  updates: readonly CompanyMargin[],
): CompanyMargin[] {
  if (updates.length === 0) return [...current];
  const updateByKey = new Map(updates.map((m) => [marginKey(m), m]));
  const merged = current.map((existing) => {
    const update = updateByKey.get(marginKey(existing));
    if (!update) return existing;
    updateByKey.delete(marginKey(existing));
    return update;
  });
  for (const remaining of updateByKey.values()) {
    merged.push(remaining);
  }
  return merged;
}

export const companyMarginsFeatureKey = 'companyMargins';

export const companyMarginsReducer = createReducer(
  INITIAL_COMPANY_MARGINS_STATE,

  on(CompanyMarginsActions.loadCompanyMargins, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(CompanyMarginsActions.loadCompanyMarginsSuccess, (state, { margins }) => ({
    ...state,
    margins,
    isLoading: false,
    error: null,
  })),

  on(CompanyMarginsActions.loadCompanyMarginsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(CompanyMarginsActions.saveCompanyMargins, (state) => ({
    ...state,
    isSaving: true,
    saveError: null,
    error: null,
    isLoading: false,
  })),

  on(CompanyMarginsActions.saveCompanyMarginsSuccess, (state, { margins }) => ({
    ...state,
    margins: mergeMargins(state.margins, margins),
    isSaving: false,
    saveError: null,
    error: null,
    isLoading: false,
  })),

  on(CompanyMarginsActions.saveCompanyMarginsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveError: error,
    isLoading: false,
  })),

  on(CompanyMarginsActions.resetState, () => INITIAL_COMPANY_MARGINS_STATE),
);

export const companyMarginsFeature = createFeature({
  name: companyMarginsFeatureKey,
  reducer: companyMarginsReducer,
});
